import {
  AlgoParams, Level1SnapshotJSON, OrderRecord, SpreadSymbol, Symbol, SecurityTypes, OrderSide, Direction,
  FuturesSnapshotPricing,
  TradingAccount,
  InstrumentDataUpdate,
} from '@qep/types/src';
import {getGreatestCommonDivisorFromArray, sumArray} from './math';

export const symbolOverrides = ['SOM'];

export const isSymbolOverride = (symbolList: string[]): boolean => {
  if (symbolList?.some(symbol => symbolOverrides.includes(symbol))) {
    return true;
  }

  return false;
};

export type AlgoConstraintRoute =
  | {kind: 'limit-price'; normalizedContractUnitComposite?: string}
  | {kind: 'net-target-notional'}
  | {kind: 'invalid'; reason: 'missing-contract-unit' | 'unsupported-contract-unit'; legIndexes: number[]};

const unsupportedContractUnits = new Set(['N/A', 'NA', 'NONE', 'UNKNOWN', 'UNSUPPORTED']);

/** Normalizes the instrument contract-unit label used to select the ATE constraint contract. */
export const normalizeContractUnit = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().replaceAll(/\s+/g, ' ').toUpperCase();
  return normalized === '' ? undefined : normalized;
};

/** Builds the established complete Frontend contract-unit descriptor for one submitted leg. */
const getCompleteContractUnitKey = (leg: Pick<OrderRecord, 'symbol'>): string | undefined => {
  const contractSize = Number(leg.symbol?.contractSize);
  const currency = leg.symbol?.currency?.trim().toUpperCase();
  const tickSize = Number(leg.symbol?.tickSize);
  if (
    !Number.isFinite(contractSize)
    || contractSize <= 0
    || !currency
    || !Number.isFinite(tickSize)
    || tickSize <= 0
  ) {
    return undefined;
  }

  return `${contractSize}:${currency}:${tickSize}`;
};

/** Selects price versus signed net-target semantics from the submitted child legs and quantities. */
export const classifyAlgoConstraintRoute = (orderLegs: Array<Pick<OrderRecord, 'quantity' | 'symbol'> & {
  contractUnits?: string | null;
}>): AlgoConstraintRoute => {
  const completeUnitKeys = orderLegs.map(getCompleteContractUnitKey);
  if (orderLegs.length <= 1) {
    return {
      kind: 'limit-price',
      ...(completeUnitKeys[0] === undefined ? {} : {normalizedContractUnitComposite: completeUnitKeys[0]}),
    };
  }

  const missingIndexes = completeUnitKeys.flatMap((unit, index) => unit === undefined ? [index] : []);
  if (missingIndexes.length > 0) {
    return {kind: 'invalid', reason: 'missing-contract-unit', legIndexes: missingIndexes};
  }

  const unsupportedIndexes = orderLegs.flatMap((leg, index) => {
    const label = normalizeContractUnit(leg.symbol?.contractUnits ?? leg.contractUnits);
    return label !== undefined && unsupportedContractUnits.has(label) ? [index] : [];
  });
  if (unsupportedIndexes.length > 0) {
    return {kind: 'invalid', reason: 'unsupported-contract-unit', legIndexes: unsupportedIndexes};
  }

  const quantities = orderLegs.map(leg => Number(removeCommaFromNumber(String(leg.quantity))));
  const hasMismatchedQuantities = new Set(quantities).size > 1;
  const distinctUnits = new Set(completeUnitKeys as string[]);
  if (distinctUnits.size <= 1 && !hasMismatchedQuantities) {
    return {kind: 'limit-price', normalizedContractUnitComposite: completeUnitKeys[0]!};
  }

  return {kind: 'net-target-notional'};
};

/** Preserves the established calculator route, including the SOM override, for existing consumers. */
export const calculateShouldUseNotional = (orderLegs: OrderRecord[], symbolList?: string[]): boolean => {
  if (symbolList?.some(symbol => symbolOverrides.includes(symbol))) {
    return false;
  }

  const compositeKeys = new Set(orderLegs.map(leg =>
    `${leg.symbol?.contractSize}:${leg.symbol?.currency}:${leg.symbol?.tickSize}`));
  return compositeKeys.size !== 1;
};

export const calculateBasketLimitPrice = (pricing: {bids: number[]; asks: number[]}, decimalPlaces: number, parentSymbol) => {
  let bidPrice;
  let askPrice;
  if (parentSymbol?.rootSymbol === 'SOM') {
    const somBids = {ZS: pricing.bids[2], ZM: pricing.bids[0], ZL: pricing.bids[1]};
    const somAsks = {ZS: pricing.asks[2], ZM: pricing.asks[0], ZL: pricing.asks[1]};
    bidPrice = calculateOveridePrice(parentSymbol.rootSymbol, {side: 'near', bids: somBids, asks: somAsks});
    askPrice = calculateOveridePrice(parentSymbol.rootSymbol, {side: 'far', bids: somBids, asks: somAsks});
  } else {
    bidPrice = sumArray(pricing.bids, decimalPlaces);
    askPrice = sumArray(pricing.asks, decimalPlaces);
  }

  return {bidPrice, askPrice};
};

export const removeCommaFromNumber = (value: string): string => {
  if (!value.replace) {
    return value;
  }

  return value?.replace(/,/g, '');
};

const chooseSideToMultiplier = (leg, orderLegs, parentSymbol) => {
  if (orderLegs.length === 1) {
    return 1;
  }

  let legSide = (leg.leg_side || leg.direction || 'BUY').toUpperCase();
  if (parentSymbol && parentSymbol.secType === SecurityTypes.FuturesSpread) {
    const defaultSide = getLegDefault(leg, parentSymbol);
    legSide = defaultSide?.leg_side;
  }

  return (legSide === 'BUY' ? 1 : -1);
};

export const calculateOrderLegPricing = (orderLegs: OrderRecord[], prices: MarketPricing, parentSymbol?: SpreadSymbol): {bids: number[]; asks: number[]} => {
  const pricing: {bids: number[]; asks: number[]} = {
    bids: [],
    asks: [],
  };

  const qtys = orderLegs.map((leg: OrderRecord) => Number(removeCommaFromNumber(leg.quantity as string)));
  const gcd = getGreatestCommonDivisorFromArray(qtys);

  orderLegs.forEach((leg: OrderRecord) => {
    const legPrice = prices[leg.key!];
    if (isSymbolOverride([parentSymbol?.rootSymbol || ''])) {
      const legQTY = Number(removeCommaFromNumber(leg.quantity as string));
      if (legQTY < 1) {
        return;
      }

      if (!legPrice?.bid?.hasOwnProperty('price')) {
        return;
      }

      if (!legPrice?.ask?.hasOwnProperty('price')) {
        return;
      }

      const baseUnit: number = 1 * 1;

      // @ts-ignore
      pricing.bids.push(baseUnit * (baseUnit > 0 ? legPrice?.bid?.price : legPrice?.ask?.price));
      // @ts-ignore
      pricing.asks.push(baseUnit * (baseUnit > 0 ? legPrice?.ask?.price : legPrice?.bid?.price));
    } else {
      const defaultLeg = getLegDefault(leg, parentSymbol);
      if (!defaultLeg && parentSymbol) {
        return;
      }

      const legQTY = Number(removeCommaFromNumber(leg.quantity as string));
      if (legQTY < 1) {
        return;
      }

      if (!legPrice?.bid?.hasOwnProperty('price')) {
        return;
      }

      if (!legPrice?.ask?.hasOwnProperty('price')) {
        return;
      }

      const baseUnit: number = Number(legQTY / gcd) * chooseSideToMultiplier(leg, orderLegs, parentSymbol);

      // @ts-ignore
      pricing.bids.push(baseUnit * (baseUnit > 0 ? legPrice?.bid?.price : legPrice?.ask?.price));
      // @ts-ignore
      pricing.asks.push(baseUnit * (baseUnit > 0 ? legPrice?.ask?.price : legPrice?.bid?.price));
    }
  });
  return pricing;
};

export interface MultiLegLimitCalculatorProps {
  orderLegs: OrderRecord[];
  changeCallback: (pricing: {bids: number[]; asks: number[]}) => void;
  userChanged?: boolean;
  algoParams?: AlgoParams;
  algoParameterId?: string;
  startPrice?: number;
  lastPrice?: number;
  parentSymbol?: SpreadSymbol | undefined;
}

type Subscriptions = Record<string, Function>;

type MarketPricing = Record<string, Level1SnapshotJSON>;
class MultiLegLimitCalculator {
  orderLegs: OrderRecord[];
  changeCallback: (pricing: {bids: number[]; asks: number[]}) => void;
  userChanged?: boolean;
  algoParams?: AlgoParams;
  algoParameterId?: string;
  startPrice?: number;
  lastPrice?: number;
  subscriptions: Subscriptions;
  marketPricing: MarketPricing;
  lastKey: string;
  shouldUseNotional: boolean;
  parentSymbol?: SpreadSymbol | undefined;

  constructor(params: MultiLegLimitCalculatorProps) {
    this.userChanged = params.userChanged ? params.userChanged : false;
    this.algoParams = params.algoParams;
    this.algoParameterId = params.algoParameterId;
    this.startPrice = params.startPrice;
    this.lastPrice = params.lastPrice;
    this.changeCallback = params.changeCallback;
    this.subscriptions = {};
    this.marketPricing = {};
    this.lastKey = '';
    this.shouldUseNotional = false;
    this.orderLegs = [];
    this.setOrderLegs(params.orderLegs);
    this.parentSymbol = params.parentSymbol; // Set in component directly at runtime, not when instantiating class.
  }

  private readonly resetLimit = () => {
    this.lastPrice = undefined;
    this.startPrice = undefined;
  };

  private readonly getOrderRecordPricing = (orderLegs: OrderRecord[], prices: MarketPricing): {bids: number[]; asks: number[]} => calculateOrderLegPricing(orderLegs, prices, this.parentSymbol);

  public setStartPrice = (startPrice: number) => {
    this.startPrice = startPrice;
  };

  public setLastPrice = (lastPrice: number) => {
    this.lastPrice = lastPrice;
  };

  public setAlgoParams = (algoParams: AlgoParams) => {
    this.algoParams = algoParams;
  };

  public setAlgoParameterId = (algoParameterId: string) => {
    this.algoParameterId = algoParameterId;
  };

  public setOrderLegs = (orderLegs: OrderRecord[]) => {
    this.orderLegs = orderLegs;

    const newSubs: Subscriptions = {...this.subscriptions};

    Object.keys(this.subscriptions).forEach((key: string) => {
      const leg: OrderRecord | undefined = this.orderLegs.find((leg: OrderRecord) => leg.key === key);
      if (!leg) {
        this.subscriptions[key]();
        delete newSubs[key];
        delete this.marketPricing[key];
      }
    });

    let localLastPrice: number | undefined;

    let orderLegKey = '';
    this.orderLegs.map((leg: OrderRecord) => orderLegKey += `${leg.symbol}_${leg.quantity}_${leg.direction}`);

    this.orderLegs.forEach((leg: OrderRecord) => {
      if (!newSubs[leg.key!]) {
        const unsub = globalThis.QEP.apiServices.marketdata.subscribeToInstrumentData(leg.symbol, (_err: unknown, data: InstrumentDataUpdate) => {
          if (!data) {
            return;
          }

          localLastPrice = this.lastPrice;
          const newMarketPricing: MarketPricing = {...this.marketPricing};
          newMarketPricing[leg.key!] = data.snapshot.snapshot;
          this.marketPricing[leg.key!] = data.snapshot.snapshot;
          const notionalPrice = calculateShouldUseNotional(this.orderLegs, [this.parentSymbol?.rootSymbol || '']);
          if (!notionalPrice) {
            let newPrices = this.getOrderRecordPricing(this.orderLegs, newMarketPricing);
            if (newPrices.bids.includes(0) || newPrices.asks.includes(0)) {
              newPrices = {bids: [], asks: []};
            }

            this.changeCallback(newPrices);
          }
        });

        newSubs[leg.key!] = unsub[0];
      }
    });

    this.subscriptions = newSubs;

    localLastPrice = this.lastPrice;
    if (this.lastKey !== orderLegKey) {
      localLastPrice = undefined;
      this.lastKey = orderLegKey;
      this.resetLimit();
    }

    this.shouldUseNotional = calculateShouldUseNotional(this.orderLegs, [this.parentSymbol?.rootSymbol || '']);
  };
}

const getLegDefault = (leg: OrderRecord, symbol?: SpreadSymbol) => {
  if (!symbol) {
    return null;
  }

  for (let i = 0; i < symbol.legs.length; i++) {
    const legSymbol = symbol.legs[i];
    if (leg.symbol?.symbol === legSymbol.qep_symbol) {
      return legSymbol;
    }
  }

  return null;
};

export default MultiLegLimitCalculator;

type BidAskData = Record<string, number>;
interface OverridePriceData {
  bids: BidAskData;
  asks: BidAskData;
  side: 'near' | 'far';
}
const calculateOveridePrice = (root: string, data: OverridePriceData, precision = 2) => {
  if (root === 'SOM') {
    const {bids, asks, side} = data;
    let base;
    if (data.side === 'near') {
      if (!bids.ZM && !bids.ZL && !asks.ZS) {
        return undefined;
      }

      base = (bids.ZM * (0.022)) + (bids.ZL * 0.11) + (asks.ZS * (-0.01));
    } else {
      if (!asks.ZM && !asks.ZL && !bids.ZS) {
        return undefined;
      }

      base = (asks.ZM * (0.022)) + (asks.ZL * 0.11) + (bids.ZS * (-0.01));
    }

    return base;
  }

  return undefined;
};

interface CalculateNotionalParams {
  parentSymbol: Symbol;
  limitPrice: number;
  legPricing: Record<string, FuturesSnapshotPricing>;
  orderLegs: Array<{
    account: TradingAccount;
    contractSize?: number;
    direction: Direction;
    key: string;
    leg_side?: unknown | Direction;
    quantity: string | number;
    symbol: Symbol;
  }>;
  isOveride: boolean;
  parentSide: Direction;
}

// Price,Symbol,isOveride,pricing for all legs,limit pricing,order legs
export const calculateNotional = (params: CalculateNotionalParams) => {
  let notionalValue: number | undefined;
  if (params.isOveride) {
    notionalValue = calculateNotionalForOverride(params.parentSymbol, params.limitPrice, params.legPricing, params.orderLegs);
  } else if (params.orderLegs[0]?.contractSize) {
    notionalValue = params.orderLegs[0]?.contractSize * params.limitPrice;
  }

  if (notionalValue === undefined) {
    return notionalValue;
  }

  const multiplier = numberFromOrderside(params.parentSide || params.orderLegs[0].leg_side);
  return notionalValue * multiplier;
};

// @todo add types
export const calculateNotionalForOverride = (parentSymbol, limitPrice, legPricing, orderLegs): number | undefined => {
  switch (parentSymbol?.rootSymbol) {
    case 'SOM': {
      const getOilPriceKey = Object.keys(legPricing).find(key => key.includes('ZL'));
      if (!getOilPriceKey) {
        console.error('No oil price found');
        return undefined;
      }

      const getOilPrice = legPricing[getOilPriceKey];
      const notional = 50000 * (limitPrice - 0.002 * getMidPrice(getOilPrice.bid.price, getOilPrice.ask.price));
      return notional;
    }

    default: {
      if (!orderLegs || Array.isArray(!orderLegs) || !orderLegs[0]) {
        return;
      }

      if (!orderLegs[0].symbol?.contractSize) {
        return undefined;
      }

      let legNotional = orderLegs[0].symbol?.contractSize * limitPrice;
      if (orderLegs[0].symbol.currency === 'USX') {
        legNotional *= 0.01;
      }

      return legNotional;
    }
  }
};

export const numberFromOrderside = (side: Direction): number => side === Direction.Buy ? 1 : -1;

const getMidPrice = (bid: number, ask: number) => (bid + ask) / 2;
